import BatchOrderItemViewModel from "../../viewModels/multiEshop/batchOrderItemViewModel";
import { observer } from "mobx-react-lite";
import { Col, Form, Jumbotron, Row } from "react-bootstrap";
import Button from "@edaz/edazkit/Button/index";
import { IconType } from "@edaz/edazkit/Icon";
import TaggedInput from "@edaz/edazkit/TaggedInput";
import React from "react";
import CountryIdInput from "../common/countryIdInput/countryIdInput";
import { isLicensePlate } from "@edaz/services/validators/isLicensePlate";
import range from "@edaz/services/validators/range";
import { View } from "@frui.ts/views";
import ValiditySelect from "../eshop/order/components/validitySelect";
import ChargeOrderItem from "models/chargeOrderItem";
import ChargeTypeVehicleInfo from "views/eshop/order/components/chargeTypeVehicleInfo";
import Status from "./components/status";
import AlertWithInfoIcon from "views/common/alertWithInfoIcon/alertWithInfoIcon";

type BatchOrderItemViewProps = {
  vm: BatchOrderItemViewModel;
  removeOrderItem: (chargeOrderItem: ChargeOrderItem) => void;
  addOrderItem?: () => void;
  index: number;
  lengthChildren: number;
};

export const BatchOrderItemView = observer(
  ({ vm, removeOrderItem, addOrderItem, index, lengthChildren }: BatchOrderItemViewProps) => {
    const tg = (code: string) => vm.localizationService.translateGeneral(`multiEshop.${code}`);
    const tPage = (code: string) => vm.localizationService.translateGeneral(`multiEshop.batchPage.${code}`);
    const ta = (code: string) => vm.localizationService.translateAttribute("orderItem", code);
    vm.fuelTypeSelectViewModel.batchIndex = index;
    vm.chargeTypeSelectViewModel.batchIndex = index;
    localStorage.removeItem("orderReloaded");

    return (
      <>
        <Jumbotron className="p-s-0 px-lg-10 py-lg-7 multi-eshop">
          {index < 1 && <Status current={1} statusSteps={vm.statusNavigation} translate={tg} />}
          <Row className="mb-4">
            {index < 1 && (
              <AlertWithInfoIcon variant="warning" className="mt-2 mb-7">
                <strong className="font-size-sm">{ta("countryWarning")}</strong>
              </AlertWithInfoIcon>
            )}
            <Col sm={6} xl={6} className="d-flex align-items-center justify-content-start mb-3">
              <h3 className="mb-0">{index < 1 ? tg("title") : `${index + 1}. ${tg("batch")}`}</h3>
            </Col>
            <Col sm={6} xl={6} className="d-flex align-items-center justify-content-end mb-3">
              {index >= 1 && (
                <div>
                  {vm.chargeOrderItem._visible && (
                    <Button
                      type="button"
                      variant="light-danger"
                      size="sm"
                      style={{ textTransform: "none" }}
                      onClick={() => removeOrderItem(vm.chargeOrderItem)}
                      iconLeftVariant="light-danger"
                      iconLeft={IconType.Cross}>
                      {tg("removeBatch")}
                    </Button>
                  )}
                </div>
              )}
              {index < 1 && (
                <Button
                  type="button"
                  variant="light-danger"
                  size="sm"
                  style={{ textTransform: "none" }}
                  onClick={vm.resetChargeOrder}
                  iconLeftVariant="light-danger"
                  iconLeft={IconType.Cross}>
                  {tg("cancel")}
                </Button>
              )}
            </Col>
          </Row>
          {vm.chargeOrderItem._visible && (
            <>
              <Form.Row className="mt-7">
                <CountryIdInput vm={vm.vehicleInfoViewModel} translateKey="multiEshop" />
              </Form.Row>
              <Form.Row>
                <Form.Group as={Col} sm={12}>
                  <Form.Label>{`${ta("licensePlate")} ${
                    vm.renderNumberOfLicensePlates ? `(${vm.renderNumberOfLicensePlates})` : ""
                  }`}</Form.Label>
                  <TaggedInput
                    target={vm.chargeOrderItem}
                    property={"licensePlates"}
                    onBlur={vm.licensePlatesFieldVisited}
                    onItemAdded={vm.addItem}
                    onItemRemoved={vm.removeItem}
                    errorMessage={vm.licensePlatesErrorMessage}
                    items={vm.licensePlates.map(licensePlate => ({
                      label: licensePlate,
                      valid:
                        isLicensePlate(licensePlate).isValid &&
                        range(licensePlate, { min: 1, max: 10 }).isValid &&
                        !vm.licensePlateHasOverlay(licensePlate),
                    }))}
                  />
                  <Form.Label className="mt-2 font-weight-normal text-muted">{tg("licensePlatesInputInfo")}</Form.Label>
                </Form.Group>
              </Form.Row>
              <div className="mb-7">
                <h4 className="mt-3 mb-2">{tg("batchPage.fuelTypeTitle")}</h4>
                <View vm={vm.fuelTypeSelectViewModel} />
                {vm.showInfoAboutVehicles && <ChargeTypeVehicleInfo markdownText={vm.translateInfoAboutVehicle("text")} />}
              </div>
              <div>
                <h4 className="mb-5">{tg("batchPage.chargeTypeTitle")}</h4>
                <Form.Row>
                  <ValiditySelect vm={vm.validitySelectViewModel} />
                </Form.Row>
              </div>
              {vm.isDifferentTimeZone && (
                <AlertWithInfoIcon variant="warning">
                  <span>{vm.translate("differentTimeZone")}</span>
                </AlertWithInfoIcon>
              )}
              <div>
                <View vm={vm.chargeTypeSelectViewModel} />
              </div>
              {vm.isSingleCharTypeToday && (
                <AlertWithInfoIcon variant="warning-dark">
                  <span>{vm.translate("isSingleCharTypeToday")}</span>
                </AlertWithInfoIcon>
              )}
            </>
          )}
        </Jumbotron>
        {(lengthChildren === index || index === -1) && (
          <Jumbotron className="p-4 px-lg-10 py-lg-7 multi-eshop">
            <Form.Row className="d-flex flex-row flex-wrap align-items-center justify-content-between">
              {vm.canAddOrderItem && (
                <>
                  <h3 className="mb-0 pt-0 pr-3 pb-3 pl-0">{tPage("newBatch")}</h3>
                  <Button
                    size="sm"
                    iconLeft={IconType.SignPlus}
                    type="button"
                    className="flex-grow-1 flex-shrink-1 flex-md-grow-0 flex-md-shrink-0"
                    variant="success"
                    style={{ textTransform: "none" }}
                    onClick={addOrderItem}>
                    {tg("addBatch")}
                  </Button>
                </>
              )}
            </Form.Row>
          </Jumbotron>
        )}
      </>
    );
  }
);
